import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import TenantService, { Tenant, UserTenantsResponse } from '../services/tenantService';

interface TenantContextType {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  defaultTenantId: string;
  isSingleTenant: boolean;
  isLoading: boolean;
  error: string | null;
  switchTenant: (tenantId: string) => void;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const SELECTED_TENANT_KEY = 'selected_tenant_id';

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [defaultTenantId, setDefaultTenantId] = useState<string>('');
  const [isSingleTenant, setIsSingleTenant] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const tenantService = TenantService.getInstance();

  const loadTenants = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data: UserTenantsResponse = await tenantService.getUserTenants();

      setAvailableTenants(data.authorized_tenants);
      setDefaultTenantId(data.default_tenant_id);
      setIsSingleTenant(data.deployment_mode === 'single');

      // Determine current tenant
      const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
      let initialTenant: Tenant | null = null;

      if (savedTenantId) {
        // Try to use saved tenant if still authorized
        initialTenant = data.authorized_tenants.find(t => t.id === savedTenantId) || null;
      }

      if (!initialTenant) {
        // Fall back to default tenant
        initialTenant = data.authorized_tenants.find(t => t.id === data.default_tenant_id) || null;
      }

      if (!initialTenant && data.authorized_tenants.length > 0) {
        // Last resort: use first available tenant
        initialTenant = data.authorized_tenants[0];
      }

      setCurrentTenant(initialTenant);

      if (initialTenant) {
        localStorage.setItem(SELECTED_TENANT_KEY, initialTenant.id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load tenants');
      console.error('Error loading tenants:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const switchTenant = (tenantId: string) => {
    const tenant = availableTenants.find(t => t.id === tenantId);
    if (tenant && tenant.id !== currentTenant?.id) {
      const previousTenant = currentTenant;
      setCurrentTenant(tenant);
      localStorage.setItem(SELECTED_TENANT_KEY, tenantId);

      // Get saved project name for the new tenant (if any)
      const savedProjectName = localStorage.getItem(`selected_project_name_${tenantId}`);

      // Emit custom event for context switch logging
      window.dispatchEvent(new CustomEvent('nomi:tenant-changed', {
        detail: {
          previous: previousTenant?.name || null,
          current: tenant.name,
          tenantId: tenant.id,
          projectName: savedProjectName || null
        }
      }));
    }
  };

  const refreshTenants = async () => {
    await loadTenants();
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const value: TenantContextType = {
    currentTenant,
    availableTenants,
    defaultTenantId,
    isSingleTenant,
    isLoading,
    error,
    switchTenant,
    refreshTenants,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
